import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, ShieldCheck, Leaf, Car, Filter, CheckCircle2, AlertTriangle, Ban, HelpCircle, ArrowRight,
} from "lucide-react";

const DOC_TYPES = [
  { key: "rc", label: "RC", icon: FileText },
  { key: "insurance", label: "INSURANCE", icon: ShieldCheck },
  { key: "puc", label: "PUC", icon: Leaf },
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "attention", label: "Needs attention" },
  { key: "expired", label: "Expired" },
  { key: "missing", label: "Missing" },
  { key: "ok", label: "All good" },
];

export default function FleetDocuments() {
  const [data, setData] = useState({ rows: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/documents/status");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const rows = data.rows || [];
    if (filter === "all") return rows;
    if (filter === "attention") return rows.filter((r) => ["warn", "expired", "missing"].includes(r.severity));
    if (filter === "expired") return rows.filter((r) => r.severity === "expired");
    if (filter === "missing") return rows.filter((r) => r.severity === "missing");
    if (filter === "ok") return rows.filter((r) => r.severity === "ok");
    return rows;
  }, [data.rows, filter]);

  const counts = data.counts || {};

  return (
    <div className="space-y-8">
      <div>
        <p className="ft-overline mb-2">FLEET COMPLIANCE</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none">
          Documents Report
        </h1>
        <p className="mt-3 text-[#A3A3A3] max-w-2xl">
          Every vehicle's RC, Insurance and PUC status in one place. Renewals within 30 days show orange, expired documents show red, missing documents show grey.
        </p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 sm:gap-6">
        <Tile label="TOTAL VEHICLES" value={counts.total || 0} icon={<Car size={14} />} testid="tile-total" />
        <Tile label="ALL GOOD" value={counts.ok || 0} icon={<CheckCircle2 size={14} className="text-emerald-400" />} testid="tile-ok" tone="ok" />
        <Tile label="RENEWAL DUE" value={counts.warn || 0} icon={<AlertTriangle size={14} className="text-[#FF5A1F]" />} testid="tile-warn" tone="warn" />
        <Tile label="EXPIRED" value={counts.expired || 0} icon={<Ban size={14} className="text-red-400" />} testid="tile-expired" tone="expired" />
        <Tile label="MISSING DOCS" value={(counts.missing || 0) + (counts.unknown || 0)} icon={<HelpCircle size={14} className="text-[#737373]" />} testid="tile-missing" />
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={14} className="text-[#737373]" />
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors border ${
              filter === f.key
                ? "bg-[#FF5A1F] text-black border-[#FF5A1F]"
                : "border-[#262626] text-[#A3A3A3] hover:text-white hover:bg-[#141414]"
            }`}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <p className="text-[#737373]">Loading fleet…</p>
      ) : filtered.length === 0 ? (
        <div className="ft-card p-10 text-center text-[#737373]">
          No vehicles match this filter.
        </div>
      ) : (
        <div className="ft-card p-0 overflow-hidden">
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full" data-testid="docs-report-table">
              <thead>
                <tr className="border-b border-[#262626] text-left">
                  <th className="ft-overline py-4 px-4">VEHICLE</th>
                  {DOC_TYPES.map((t) => (
                    <th key={t.key} className="ft-overline py-4 px-4">{t.label}</th>
                  ))}
                  <th className="py-4 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <VehicleRow key={row.vehicle_id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile stack */}
          <div className="md:hidden divide-y divide-[#262626]">
            {filtered.map((row) => (
              <VehicleCardMobile key={row.vehicle_id} row={row} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VehicleRow({ row }) {
  return (
    <tr
      className="border-b border-[#262626] hover:bg-[#1A1A1A] transition-colors"
      data-testid={`fleet-row-${row.vehicle_id}`}
    >
      <td className="py-4 px-4">
        <Link to={`/vehicles/${row.vehicle_id}`} className="hover:text-[#FF5A1F] transition-colors">
          <p className="font-mono font-bold text-white">{row.vehicle_number}</p>
          <p className="text-xs text-[#737373] mt-0.5">{row.vehicle_name || ""}</p>
        </Link>
      </td>
      {DOC_TYPES.map((t) => (
        <td key={t.key} className="py-4 px-4">
          <DocPill entry={row.documents[t.key]} docType={t.key} />
        </td>
      ))}
      <td className="py-4 px-4 text-right">
        <Link
          to={`/vehicles/${row.vehicle_id}`}
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#A3A3A3] hover:text-[#FF5A1F] transition-colors"
          data-testid={`fleet-open-${row.vehicle_id}`}
        >
          Open <ArrowRight size={12} />
        </Link>
      </td>
    </tr>
  );
}

function VehicleCardMobile({ row }) {
  return (
    <Link
      to={`/vehicles/${row.vehicle_id}`}
      className="block p-4 hover:bg-[#141414] transition-colors"
      data-testid={`fleet-card-${row.vehicle_id}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono font-bold text-white">{row.vehicle_number}</p>
          <p className="text-xs text-[#737373] mt-0.5">{row.vehicle_name || ""}</p>
        </div>
        <ArrowRight size={16} className="text-[#737373]" />
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        {DOC_TYPES.map((t) => (
          <div key={t.key} className="space-y-1">
            <p className="text-[9px] uppercase tracking-wider text-[#737373] font-mono">{t.label}</p>
            <DocPill entry={row.documents[t.key]} docType={t.key} compact />
          </div>
        ))}
      </div>
    </Link>
  );
}

function DocPill({ entry, docType, compact }) {
  if (!entry?.present) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-1 ${compact ? "text-[10px]" : "text-xs"} font-semibold border border-[#262626] bg-[#141414] text-[#737373]`}
      >
        <HelpCircle size={12} />
        {compact ? "Missing" : "Not uploaded"}
      </span>
    );
  }

  // For RC there is no expiry-based state; treat as OK when present.
  if (docType === "rc") {
    return (
      <div>
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-1 ${compact ? "text-[10px]" : "text-xs"} font-semibold border border-emerald-500/30 bg-emerald-500/10 text-emerald-300`}
        >
          <CheckCircle2 size={12} /> On file
        </span>
        {!compact && entry.registration_number && (
          <p className="text-[10px] text-[#737373] font-mono mt-1 truncate max-w-[16ch]">{entry.registration_number}</p>
        )}
      </div>
    );
  }

  const days = entry.days_left;
  let variant = "ok";
  let icon = <CheckCircle2 size={12} />;
  let label = days != null ? `Valid ${days}d` : "Uploaded";

  if (days == null) {
    variant = "unknown";
    icon = <HelpCircle size={12} />;
    label = "Date not read";
  } else if (days < 0) {
    variant = "expired";
    icon = <Ban size={12} />;
    label = `Expired ${Math.abs(days)}d ago`;
  } else if (days <= 30) {
    variant = "warn";
    icon = <AlertTriangle size={12} />;
    label = `Renew in ${days}d`;
  }

  const cls = {
    ok: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    warn: "border-[#FF5A1F]/40 bg-[#FF5A1F]/10 text-[#FF5A1F]",
    expired: "border-red-500/40 bg-red-500/10 text-red-300",
    unknown: "border-[#262626] bg-[#141414] text-[#A3A3A3]",
  }[variant];

  return (
    <div>
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-1 ${compact ? "text-[10px]" : "text-xs"} font-semibold border ${cls}`}
      >
        {icon} {label}
      </span>
      {!compact && (
        <p className="text-[10px] text-[#737373] font-mono mt-1 truncate max-w-[18ch]">
          {docType === "insurance" ? (entry.insurer || entry.policy_number || "—") : (entry.testing_center?.split(",")[0] || entry.certificate_number || "—")}
          {entry.expiry_date ? ` · ${String(entry.expiry_date).slice(0, 10)}` : ""}
        </p>
      )}
    </div>
  );
}

function Tile({ label, value, icon, testid, tone }) {
  const border = tone === "warn" ? "border-l-4 border-l-[#FF5A1F]" : tone === "expired" ? "border-l-4 border-l-red-500" : tone === "ok" ? "border-l-4 border-l-emerald-500" : "";
  return (
    <div className={`ft-card p-5 ${border}`} data-testid={testid}>
      <div className="flex items-center justify-between text-[#737373]">
        <span className="ft-overline">{label}</span>
        {icon}
      </div>
      <p className="font-mono text-3xl font-medium mt-3 text-white">{value}</p>
    </div>
  );
}
